import { BCMSCurrency, BCMSDate, BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, Branding, useBCMSContext, useModel, useRoute, useStateManager } from "@bcm-dev/bcms-vite";
import { CenterContentContainer, ContentHeaderView, PageContainer, PageContentView } from "components/shared";
import { PrimaryButton } from "components/ui";
import Classes, { Account, ACHBatch, ACHFromAccount, ACHToParticipant, CompanyAccount, Participant } from "model";
import React from "react";
import OneTimeACHFormSection from "components/pages/OneTimeACHPaymentPage/components/OneTimeACHFormSection";
import OneTimeACHToSection from "components/pages/OneTimeACHPaymentPage/components/OneTimeACHToSection";
import ACHTransfersFromSection, { ACHTransfersFromSectionSingleAccount } from "components/shared/widgets/ACHWidgets/ACHTransfersFromSection";
import "./mobile.css";
import "./styles.css";

/**
 * @function OneTimeACHPaymentPage
 * @return {React.Component}
 * @exported true
 * @description The main component for OneTimeACHPaymentPage
 */
export default function OneTimeACHPaymentPage(props) {
    const globalController = useBCMSContext();
    const navigate = useRoute();
    
    BCMSRecord.globalControllerFor(Classes, globalController);

    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };

    const { state } = globalController ?? {};
    const { branding } = state ?? {};
    const { options } = branding ?? {};

    // Returns instances of ACHFromAccount.
    const addACHFromAccount = _data => {
        let achFromAccounts = _data.achFromAccounts.accounts;
        const selectedOffsetAccount = _data.selectedOffsetAccount.account;

        const getNewID = id => {
            if(achFromAccounts.length === 0) return ACHFromAccount.nextID();
            else {
                const isExistingID = achFromAccounts.find(({ id: accountID }) => accountID == id) !== undefined;
                return isExistingID ? getNewID(id + 1) : id;
            }
        };

        const addedAccount = ACHFromAccount.init({
            account: selectedOffsetAccount.id,
            id: getNewID(ACHFromAccount.nextID())
        });

        achFromAccounts = [ addedAccount, ...achFromAccounts ];
        return achFromAccounts;
    };

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onChangeUIProp: prop => val => {
            BCMSStateManagerCRUD.init(state)
            .update([ "ui", prop, "isActive" ], val)
            .submit(setState);
        },

        onChangeDataProp: prop => val => {
            const crud = BCMSStateManagerCRUD.init(state);
            if(prop === "scheduleType" && (val === "immediate" || val === "future dated")) {
                crud.update([ "data", "oneTimeACHPayment", "effectiveDate" ], BCMSDate.init().nextBusinessDay());
            } else if(prop === "scheduleType" && val === "same day") {
                crud.update([ "data", "oneTimeACHPayment", "effectiveDate" ], BCMSDate.init());
            }

            if(prop === "offsetAccount") {
                const selectedOffsetAccount = Account.initByID(val);
                crud.update([ "data", "selectedOffsetAccount", "account" ], selectedOffsetAccount);
            } else {
                crud.update([ "data", "oneTimeACHPayment", prop ], val);
            }
            
            crud.submit(setState);
        },

        onACHFromAccountChange: (achFromAccount) => val => {
            const findIndex = data.achFromAccounts.accounts.findIndex(item => item.id === achFromAccount.id);
            const updateAccount = ACHFromAccount.init({ ...achFromAccount.record, amount: val.amount });
            const achFromAccounts = [
                ...data.achFromAccounts.accounts.slice(0, findIndex),
                updateAccount,
                ...data.achFromAccounts.accounts.slice(findIndex + 1)
            ];
    
            const debitAmount = achFromAccounts.reduce((acc, item) => {
                const currAmount = item.amount.amount;
                if(currAmount) return acc + parseFloat(currAmount);
                return acc;
            }, 0);

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "achFromAccounts", "accounts" ], achFromAccounts)
            .update([ "data", "oneTimeACHPayment", "debitAmount" ], debitAmount)
            .submit(setState);
        },

        onACHFromAccountDelete: (achFromAccount) => val => {
            const achFromAccounts = data.achFromAccounts.accounts.filter(item => item.id !== achFromAccount.id);
            const updatedOffsetAccounts = getDefaultData().offsetAccounts.filter(item => {
                const currIDs = achFromAccounts.reduce((acc, _item) => {
                    acc.push(_item.account.id);
                    return acc;
                }, []);
                return !currIDs.includes(item.id);
            });


            BCMSStateManagerCRUD.init(state)
            .update([ "data", "offsetAccounts" ], updatedOffsetAccounts)
            .update([ "data", "achFromAccounts", "accounts" ], achFromAccounts)
            .submit(setState);
        },

        onAddOffsetAccountClick: () => {
            if(!state.data.selectedOffsetAccount.account) return;
            const achFromAccounts = addACHFromAccount(state.data);

            const updatedOffsetAccounts = getDefaultData().offsetAccounts.filter(item => {
            const currIDs = achFromAccounts.reduce((acc, _item) => {
                acc.push(_item.account.id);
                return acc;
            }, []);

            return !currIDs.includes(item.id);
        });

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "offsetAccounts" ], updatedOffsetAccounts)
            .update([ "data", "achFromAccounts", "accounts" ], achFromAccounts)
            .update([ "data", "selectedOffsetAccount", "account" ], undefined)
            .submit(setState);
        },

        onParticipantsModalAddSelectedParticipantsClick: (participantsInModal) => {
            let achToParticipants = state.data.achToParticipants.participants;
            let nextID = undefined;
            if(achToParticipants.length === 0) {
                // check ids globally
                nextID = ACHToParticipant.nextID();
            } else {
                nextID = parseFloat(achToParticipants[achToParticipants.length - 1].id) + 1;
            }

            const existingAchToParticipantIDs = data.achToParticipants.participants.reduce((acc, item) => {
                acc.push(item.id);
                return acc;
            }, []);

            /*if(existingAchToParticipantIDs.includes(nextID)) {
                nextID += 1;
            }*/

            (function determineID(id) {
                if(existingAchToParticipantIDs.includes(id)) { determineID(id + 1); }
                else { nextID = id; }
            })(nextID);

            const updatedACHToParticipants = participantsInModal.reduce((acc, item) => {
                const added = ACHToParticipant.init({ id: nextID, participant: item.id,
                    oneTimeACHPayment: data.oneTimeACHPayment, notify: "No", addenda: "0" });
                nextID = parseFloat(nextID) + 1;
                acc.push(added);
                return acc;
            }, []);

            achToParticipants = [ ...updatedACHToParticipants, ...achToParticipants ];

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "achToParticipants", "participants" ], achToParticipants)
            .submit(setState);
        },

        onParticipantModalCancelOnlyClick: () => {
            BCMSStateManagerCRUD.init(state)
            .update([ "ui", "achParticipantsModalIsOpen", "isActive" ], false)
            .submit(setState);
        },

        onParticipantModalCancelClick: () => {
            BCMSStateManagerCRUD.init(state)
            .update([ "ui", "achParticipantsModalIsOpen", "isActive" ], false)
            .submit(setState);
        },

        onAddEntryModalCancelClick: () => {
            BCMSStateManagerCRUD.init(state)
                .update([ "ui", "addEntryModalIsOpen", "isActive"], false)
                .submit(setState);
        },

        onACHToParticipantDelete: (achToParticipant) => val => {
            const achToParticipants = state.data.achToParticipants.participants.filter(item => item.id !== achToParticipant.id);
            const creditAmount = achToParticipants.reduce((acc, item) => {
                const currAmount = item.amount.amount;
                if(currAmount) return acc + parseFloat(currAmount);
                return acc;
            }, 0);

          const updates = BCMSStateManagerCRUD.init(state)
            .update([ "data", "achToParticipants", "participants" ], achToParticipants)
            .update([ "ui", "achParticipantsModalIsOpen", "isActive" ], false)             

        if(options.offset_account_ui_abi22 === "Single Account") {
            updates.update([ "data", "oneTimeACHPayment", "debitAmount" ], BCMSCurrency.init(creditAmount));
        }

        updates.submit(setState);
        },

        onACHToParticipantChange: (prop, achToParticipant) => val => {
            const findIndex = state.data.achToParticipants.participants.findIndex(item => item.id === achToParticipant.id);
            const updateACHToParticipant = ACHToParticipant.init({ ...achToParticipant.record, amount: val.amount });

            const achToParticipants = [
                ...state.data.achToParticipants.participants.slice(0, findIndex),
                updateACHToParticipant,
                ...state.data.achToParticipants.participants.slice(findIndex + 1)
            ];

            const creditAmount = achToParticipants.reduce((acc, item) => {
                const currAmount = item.amount.amount;
                if(currAmount) return acc + parseFloat(currAmount);
                return acc;
            }, 0);

            const updates = BCMSStateManagerCRUD.init(state)
                .update([ "data", "achToParticipants", "participants" ], achToParticipants)
                .update([ "data", "oneTimeACHPayment", "creditAmount" ], creditAmount);

            if(options.offset_account_ui_abi22 === "Single Account") {
                updates.update([ "data", "oneTimeACHPayment", "debitAmount" ], BCMSCurrency.init(creditAmount));
            }

            updates.submit(setState);
        },

        onAddParticipantsOpenModalClick: () => {
            BCMSStateManagerCRUD.init(state)
            .update([ "ui", "achParticipantsModalIsOpen", "isActive" ], true)
            .submit(setState);
        },

        onAddEntryOpenModalClick: () => {
            BCMSStateManagerCRUD.init(state)
                .update([ "ui", "addEntryModalIsOpen", "isActive" ], true)
                .submit(setState);
        },

        onInitiate: () => {
            let debitAmount = parseFloat(state.data.oneTimeACHPayment.debitAmount.amount);
            let creditAmount = parseFloat(state.data.oneTimeACHPayment.creditAmount.amount);
            const hasACHFromSection = options.ach_from_abi22 === "true";
            let _achFromAccounts = state.data.achFromAccounts;

            // If no "ACH From" section is shown, create an "achFrom" entry in the model
            // and treat the amount as if "0" was set.
            if(!hasACHFromSection) {
                const selectedOffsetAccount = Account.initAllBusinessAsset()[0];
                state.data.selectedOffsetAccount = { account: selectedOffsetAccount };
                const achFromAccounts = addACHFromAccount(state.data);
                _achFromAccounts = { accounts: achFromAccounts };
            }

            // START: Credit and Debit total amounts must match.
            if(debitAmount === 0) {
                if(creditAmount > debitAmount) {
                    data.oneTimeACHPayment.debitAmount = creditAmount;
                } else if(creditAmount === 0) {
                    data.oneTimeACHPayment.debitAmount = "5000";
                    data.oneTimeACHPayment.creditAmount = "5000";
                }
            } else if(debitAmount > 0) {
                // Credit and Debit total amounts must match.
                if(creditAmount !== debitAmount) {
                    data.oneTimeACHPayment.creditAmount = debitAmount;
                }
            }
            // END: Credit and Debit total amounts must match.

            const achFromAccountUpdates = _achFromAccounts.accounts.reduce((acc, item) => {
                acc.push(ACHFromAccount.create({ ...item.record, oneTimeACHPayment: state.data.oneTimeACHPayment.id }));
                return acc;
            }, []);

            const achToParticipantsUpdates = state.data.achToParticipants.participants.reduce((acc, item) => {
                acc.push(ACHToParticipant.create({ ...item.record, oneTimeACHPayment: state.data.oneTimeACHPayment.id }));
                return acc;
            }, []);
           
            const addedOneTimeACHPayment = ACHBatch.init({ ...state.data.oneTimeACHPayment.record});
            
            const allOneTimeACHPayments = ACHBatch.initAll()
                .filter(item => item.id !== undefined)
                .sort((a, b) => parseFloat(b.referenceNumber) - parseFloat(a.referenceNumber))
                let updatedRefNumber = state.data.oneTimeACHPayment.referenceNumber;
       
                if(allOneTimeACHPayments.length > 0) {
                    updatedRefNumber = parseFloat(allOneTimeACHPayments[0].referenceNumber) + 1;
                }
                
            addedOneTimeACHPayment.referenceNumber = updatedRefNumber;
    
            const mergedACHPayment = BCMSDefaultModel.mergeRecords(addedOneTimeACHPayment, 
                { id: ACHBatch.nextID(), company: CompanyAccount.initByID(0) });

            BCMSRecord.batch([
                ...achFromAccountUpdates,
                ...achToParticipantsUpdates,
                ACHBatch.create(mergedACHPayment)
            ], globalController, Classes);
            navigate("/achActivity", { state : { addedOneTimeACHPayment : mergedACHPayment, oneTimeACHPaymentSubmitted: true },  })
        }
    });

    return (
        <PageContainer className="one-time-ach-payment-page" state={ { data, ui } } controller={ controller }>
            <PageContent/>
        </PageContainer>
    )
}

export const getDefaultData = () => ({
    oneTimeACHPayment: ACHBatch.init({ 
        id: ACHBatch.nextID(), 
        name: "One-Time",
        scheduleType: "immediate", 
        pendingApprovals: "1",
        effectiveDate: BCMSDate.init().nextBusinessDay(),
        status:"pending approval",
        creditAmount: "0", 
        debitAmount: "0"
    }),
    companyAccounts: CompanyAccount.initAll(),
    offsetAccounts: Account.initAllBusinessAsset(),
    achFromAccounts: {
        accounts: [] // always empty by default since we are making a new payment.
    }, 
    achToParticipants: {
        participants: [] // always empty by default since we are making a new payment.
    }, 
    participants: Participant.initAll().map(item => {
        // "isSelected" will be used to keep track of row selection.
        item.isSelected = undefined;
        return item;
    }),
    allParticipants: Participant.initAll().map(item => {
        // "isSelected" will be used to keep track of row selection.
        item.isSelected = undefined;
        return item;
    }),
    groupSearchValue: "",
    selectedOffsetAccount: { account: undefined }
});

export const getDefaultUI = () => ({
    achParticipantsModalIsOpen: { isActive: false },
    transactionTypeSelectIsActive: { isActive: false },
    secCodeSelectIsActive: { isActive: false },
    companySelectIsActive: { isActive: false },
    effectiveDateSelectIsActive: { isActive: false },
    offsetAccountSelectIsActive: { isActive: false },
    showOneTimeACHModal: { isActive: true },
    showSecCode: { isActive: false },
    scheduleSelectIsActive: { isActive: false },
    addEntryModalIsOpen: { isActive: false }
});

function PageContent(props) {
    const { data, ui, onChangeDataProp, onChangeUIProp, onAddOffsetAccountClick, 
    onACHFromAccountChange, onACHFromAccountDelete, onParticipantRowChange, onParticipantModalCancelClick,
     onParticipantModalCancelOnlyClick,
    onParticipantsModalAddSelectedParticipantsClick, onACHToParticipantChange, onACHToParticipantDelete,
    onSelectAllParticipantsCheckboxChange, onfilteredParticipantsInSearch, onAddParticipantsOpenModalClick, 
    onInitiate, onParticipantGroupInputChange, onParticipantGroupFilterClick, onAddEntryOpenModalClick,
    onAddEntryModalCancelClick } = useStateManager();
    const { branding } = useModel() ?? {};
    const { options } = branding ?? {};
    const hasACHFromSection = options.ach_from_abi22 === "true";

    // An object that contains the functions for the page controller.
    const action = { action: {
            onChangeDataProp, onChangeUIProp, onAddOffsetAccountClick, 
            onACHFromAccountChange, onACHFromAccountDelete, onParticipantRowChange, onParticipantModalCancelClick, 
            onParticipantModalCancelOnlyClick,
            onParticipantsModalAddSelectedParticipantsClick, onACHToParticipantChange, onACHToParticipantDelete,
            onSelectAllParticipantsCheckboxChange, onfilteredParticipantsInSearch, onAddParticipantsOpenModalClick,
            onParticipantGroupInputChange, onParticipantGroupFilterClick, onAddEntryOpenModalClick, onAddEntryModalCancelClick
        } 
    };
    
    return (
        <PageContentView>
            <CenterContentContainer>
                <ContentHeaderView text="One-Time ACH Payment" />

                {/* ACH form section. Note: OneTimeACHModal opens in this component and needs hooked up. */}
                <OneTimeACHFormSection ui={ ui } data={ data } { ...action } />

                {/* ACH From section */}
                { hasACHFromSection
                && <>
                    <Branding id="offset_account_ui_abi22" exp={ value => value !== "Single Account" } default="Multi-Accounts">
                        <ACHTransfersFromSection ui={ ui } data={ data } { ...action } />
                    </Branding> 

                    <Branding id="offset_account_ui_abi22" value="Single Account" default="Single Account">
                        <ACHTransfersFromSectionSingleAccount ui={ ui } data={ data } { ...action } debitAmount={ data.oneTimeACHPayment.debitAmount } />
                    </Branding>
                </>
                }

                {/* ACH To section */}
                <OneTimeACHToSection ui={ ui } data={ data } { ...action } />

                <div className="one-time-ach-payment-section button-section">
                    <PrimaryButton text="Initiate" dataPoint={ `One_Time_ACH_Payment_Initiate_Button` } onClick={onInitiate} />
                </div>
            </CenterContentContainer>
        </PageContentView>
    );
}